import { useCallback, useRef, useState } from "react";
import { Upload, FileText, Loader2, X, AlertTriangle } from "lucide-react";
import { parseCensusFile, type CensusError, type CensusParseOptions } from "../validation/censusValidation";
import { validateClaimsCensusFile } from "../validation/claimsCensusValidation";
import { formatFileSize } from "./fields/fileUploadConstants";
import type { Employee, DocumentRef } from "../types/form";

const MAX_ERRORS_SHOWN = 10;
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200 MB
const ACCEPTED_EXTENSIONS = ["xlsx", "xls"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(",");

interface CensusUploadProps {
  censusFile: DocumentRef | null;
  removing?: boolean;
  parseOptions?: CensusParseOptions;
  /**
   * Claims-based (UW_Method__c = Claims) upload. The file is validated against
   * the claims template and attached as-is — never parsed into employees, so
   * `onParsed` is always called with an empty array.
   */
  claims?: boolean;
  onParsed: (employees: Employee[], file: File) => void;
  onRemove: () => void;
}

export function CensusUpload({ censusFile, removing, parseOptions, claims, onParsed, onRemove }: CensusUploadProps) {
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<CensusError[]>([]);
  const [totalErrorRows, setTotalErrorRows] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const clearErrors = useCallback(() => {
    setErrorMessage(null);
    setRowErrors([]);
    setTotalErrorRows(0);
  }, []);

  const handleFile = useCallback(async (file: File) => {
    clearErrors();

    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setErrorMessage("Incorrect file type uploaded. Please download and use the census template above.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage("File exceeds the 200 MB size limit. Please reduce the file size and try again.");
      return;
    }

    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();

      // Claims census is validated only (right template + has records) and
      // attached as-is — no per-row parsing, so there are no row-level errors.
      if (claims) {
        const result = validateClaimsCensusFile(buffer);
        if (!result.success) {
          setErrorMessage(result.errorMessage);
          return;
        }
        onParsed([], file);
        return;
      }

      const result = parseCensusFile(buffer, parseOptions);

      if (!result.success) {
        if (result.errorMessage) {
          setErrorMessage(result.errorMessage);
        } else {
          setRowErrors(result.errors);
          setTotalErrorRows(result.totalErrorRows);
        }
        return;
      }

      onParsed(result.employees, file);
    } finally {
      setParsing(false);
    }
  }, [claims, clearErrors, onParsed, parseOptions]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (parsing) return;
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  }, [handleFile, parsing]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    if (inputRef.current) inputRef.current.value = "";
  }, [handleFile]);

  if (censusFile) {
    return (
      <div>
        <div className="flex items-center gap-3 border rounded-lg px-4 py-3 bg-gray-50 border-gray-200">
          <FileText size={20} className="text-gray-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{censusFile.name}</p>
            <p className="text-xs text-gray-500">
              {removing ? "Removing…" : formatFileSize(censusFile.size)}
            </p>
          </div>
          {removing ? (
            <div className="min-h-[44px] min-w-[44px] flex items-center justify-center shrink-0">
              <Loader2 size={18} className="text-gray-400 animate-spin" />
            </div>
          ) : (
            <button
              onClick={onRemove}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-danger transition-colors cursor-pointer shrink-0"
              aria-label={`Remove ${censusFile.name}`}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
    );
  }

  const errorsShown = rowErrors.slice(0, MAX_ERRORS_SHOWN);
  const hasErrors = !!errorMessage || rowErrors.length > 0;

  return (
    <div>
      <label
        className={`flex flex-col items-center gap-2 border-2 border-dashed rounded-lg px-4 py-6 cursor-pointer transition-all active:scale-[0.98] focus-within:ring-2 focus-within:ring-brand-green-50 focus-within:border-brand-green ${
          dragOver
            ? "border-brand-green bg-brand-green-05"
            : hasErrors
              ? "border-danger bg-danger-bg"
              : "border-gray-300 hover:border-brand-green hover:bg-brand-green-05"
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
      >
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={ACCEPT_ATTR}
          onChange={handleInputChange}
          disabled={parsing}
        />
        {parsing ? (
          <Loader2 size={24} className="text-brand-green animate-spin" />
        ) : (
          <Upload size={24} className="text-gray-400" />
        )}
        <span className="text-sm font-medium text-gray-600" aria-live="polite">
          {parsing ? "Processing..." : "Tap to upload or drag & drop"}
        </span>
        <span className="text-xs text-gray-500">XLSX, XLS — Max 200MB</span>
      </label>

      {errorMessage && (
        <div className="flex items-start gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-danger">{errorMessage}</p>
        </div>
      )}

      {rowErrors.length > 0 && (() => {
        const grouped = new Map<number, { name: string; errors: typeof errorsShown }>();
        for (const err of errorsShown) {
          if (!grouped.has(err.row)) grouped.set(err.row, { name: err.name, errors: [] });
          grouped.get(err.row)!.errors.push(err);
        }
        return (
          <div className="mt-3 border border-red-200 rounded-lg overflow-hidden" role="alert">
            <div className="flex items-start justify-between gap-2 p-4 bg-red-50">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
                <p className="text-sm font-bold text-gray-900">
                  No records were uploaded. {totalErrorRows} row{totalErrorRows !== 1 ? "s" : ""} in the file have invalid data.
                  Please correct the file and re-upload.
                  {rowErrors.length > MAX_ERRORS_SHOWN && ` Showing first ${MAX_ERRORS_SHOWN} of ${rowErrors.length} errors.`}
                </p>
              </div>
              <button
                onClick={clearErrors}
                className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                aria-label="Dismiss errors"
              >
                <X size={16} />
              </button>
            </div>
            <div className="divide-y divide-red-100">
              {[...grouped.entries()].map(([row, { name, errors: rowErrs }]) => (
                <div key={row} className="px-4 py-3 bg-red-50/50">
                  <p className="text-sm font-bold text-gray-900 mb-1">Row {row} - {name}</p>
                  {rowErrs.map((err, i) => (
                    <p key={i} className="text-xs text-gray-600 ml-2">
                      · {err.column}: <span className="text-danger">&quot;{err.value}&quot;</span> — expected {err.expected}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
